// Catalog.ID Client — ceremony signer: who signs, with which key, into which file
// ============================================================================
// docs/cid-governance.md M6, D6, D8, D20. Whose key a ceremony document needs,
// whether the session holds it, how that key is reached, and the file the
// Developer's `cl-root` reads:
//
//   • certificate countersignature → the request file in
//     `CertificateRequests.ToFile` shape, with `organization: { org_cid,
//     signature }` added (`cl-root certify --request`);
//   • binding consent and bulk migration consent → `{ consent, org_signature }`
//     (`cl-root bind --consent`, `cl-root migrate-bulk --consent`);
//   • account migration consent → `{ consent, account_signature }`
//     (`cl-root migrate --consent`).
//
// Signer population is narrow by construction, not by an ACL: the
// organization's `main_signing_key` sits in the organization account's own key
// bag, and a delegate receives a wrapped copy only as an OWNER
// (`KnownKeyTypes.GetEntitledOrgKeys`; mirror `state/org-key-entitlements.ts`).
// An admin is refused here with the reason rather than at the key fetch. A
// person's own migration consent is signed from their own key bag.
//
// The session is read from the node (`/api/account/me`), never from the
// identity state a login leaves behind: a fresh login at `/ceremony` records
// the CID and the account type only, and deciding on that refused every owner
// delegate (M6).

import {
    HYBRID_PROFILE,
    accountMigrationConsentFile,
    accountMigrationConsentSigningBytes,
    bindingConsentFile,
    bindingConsentSigningBytes,
    bulkMigrationConsentFile,
    bulkMigrationConsentSigningBytes,
    certificateCountersignatureBytes,
    certificateRequestFile,
    checkCertificateRequest,
    isOrganizationCid,
    readAccountMigrationConsentFile,
    readBindingConsentFile,
    readBulkMigrationConsentFile,
    readCertificateRequestFile,
    federationSignatureVerifier,
    type SignatureVerifier,
    type SignedCertificateRequest,
} from '@catalog-federation';
import {
    combinedToWrapper, signHybrid, signingPublicKeyFromSeed, verifyHybrid, wrapperToCombinedBytes,
} from '../crypto/signing';
import * as aes from '../crypto/aes';
import { unwrapPrivateKey } from '../crypto/claims';
import { keys } from '../state/keys';
import * as accountApi from '../api/account';
import * as delegateApi from '../api/delegate';
import type { CeremonyDocument } from './payload';

/** The key every ceremony document is signed with. */
export const MAIN_SIGNING_KEY = 'main_signing_key';
/** Org keys are wrapped to each delegate's attr_encrypt public key. */
const DELEGATE_UNWRAP_KEY = 'attr_encrypt';

/** The verifier the console checks proofs and its own output with: the
 *  client's hybrid primitive over the combined wire form, ES256 by Web Crypto. */
export const ceremonySignatureVerifier: SignatureVerifier = federationSignatureVerifier(
    (message, signature, publicKey) => verifyHybrid(message, combinedToWrapper(signature), publicKey),
);

// ── the session ─────────────────────────────────────────────────────────

/** What the signer gate reads about the session. */
export interface CeremonySessionInput {
    accountType: string;
    cid: string;
    organizationCid?: string | null;
    roles?: readonly string[];
}

/** The session as the node reports it — roles and bound codes included. */
export interface CeremonySession extends CeremonySessionInput {
    organizationCid: string | null;
    roles: string[];
    /** Every operator code bound to the account's organization (D12). */
    operatorCodes: string[];
}

/** Reads the session from the node (`/api/account/me`). Throws when there is none. */
export async function loadCeremonySession(): Promise<CeremonySession> {
    const me = await accountApi.getAccountInfo();
    return {
        accountType: me.accountType,
        cid: me.cid,
        organizationCid: me.organizationCid ?? null,
        roles: me.roles ?? [],
        operatorCodes: me.operatorCodes ?? [],
    };
}

// ── whose key ───────────────────────────────────────────────────────────

/** Whose `main_signing_key` a document needs: an organization's (named, or
 *  the session's own for a countersignature), or a person's own. */
export type SignerNeed =
    | { kind: 'organization'; orgCid: string | null }
    | { kind: 'account'; accountCid: string };

export function signerNeedOf(doc: CeremonyDocument): SignerNeed {
    switch (doc.kind) {
        case 'certificate_countersignature':
            return { kind: 'organization', orgCid: null };
        case 'binding_consent':
        case 'bulk_migration_consent':
            return { kind: 'organization', orgCid: doc.consent.orgCid };
        case 'account_migration_consent':
            // An organization moves by its own key, which owners hold; a
            // person by theirs. Delegates and agents never move alone.
            return isOrganizationCid(doc.consent.accountCid)
                ? { kind: 'organization', orgCid: doc.consent.accountCid }
                : { kind: 'account', accountCid: doc.consent.accountCid };
    }
}

export type KeySource = 'keybag' | 'wrapped-org-key';

export type SignerRefusalCode =
    | 'not-an-org-session'
    | 'delegate-not-owner'
    | 'no-org-context'
    | 'organization-mismatch'
    | 'account-mismatch';

export type SignerResolution =
    | {
        eligible: true;
        /** The organization or account whose key signs. */
        signerCid: string;
        /** `keybag` = the session's own bag; `wrapped-org-key` = an owner delegate's copy. */
        keySource: KeySource;
    }
    | { eligible: false; code: SignerRefusalCode; message: string };

/**
 * Decides whether the session may sign what `need` names, and where the key
 * comes from. A refusal always carries its reason.
 */
export function resolveCeremonySigner(session: CeremonySessionInput, need: SignerNeed): SignerResolution {
    if (need.kind === 'account') {
        if (session.accountType === 'natural_person' && session.cid === need.accountCid) {
            return { eligible: true, signerCid: session.cid, keySource: 'keybag' };
        }
        return {
            eligible: false,
            code: 'account-mismatch',
            message: `This consent is signed by account ${need.accountCid} with its own key, but you are signed in as `
                + `${session.cid}. Sign in as ${need.accountCid} and open the file again.`,
        };
    }

    let orgCid: string;
    let keySource: KeySource;
    if (session.accountType === 'organization') {
        orgCid = session.cid;
        keySource = 'keybag';
    } else if (session.accountType === 'delegate') {
        const roles = session.roles ?? [];
        if (!roles.includes('owner')) {
            return {
                eligible: false,
                code: 'delegate-not-owner',
                message:
                    'Signing needs the organization\'s signing key, which only owner delegates hold. '
                    + `This session is a delegate with ${roles.length > 0 ? `the role(s) ${roles.join(', ')}` : 'no roles'}. `
                    + 'Sign in as an owner delegate or as the organization account.',
            };
        }
        if (!session.organizationCid) {
            return { eligible: false, code: 'no-org-context', message: 'This delegate session is not attached to an organization.' };
        }
        orgCid = session.organizationCid;
        keySource = 'wrapped-org-key';
    } else {
        return {
            eligible: false,
            code: 'not-an-org-session',
            message: 'This is signed with an organization\'s key. Sign in as an owner delegate of the organization, '
                + 'or as the organization account.',
        };
    }

    if (need.orgCid !== null && need.orgCid !== orgCid) {
        return {
            eligible: false,
            code: 'organization-mismatch',
            message: `This document is for organization ${need.orgCid}, but you are signed in for ${orgCid}. `
                + `Sign in for ${need.orgCid}, or ask for a document for ${orgCid}.`,
        };
    }
    return { eligible: true, signerCid: orgCid, keySource };
}

// ── the key ─────────────────────────────────────────────────────────────

export interface WrappedOrgKey {
    keyId: string;
    keyIndex: number;
    wrappedKey: string;
}

/**
 * The copy of `keyId` with the LOWEST keyIndex. `/api/delegate/wrappedkeys`
 * returns rows in no guaranteed order, and taking the first one hands out a
 * post-rotation copy once a key has been rotated (the rule `state/org-keys.ts`
 * and `apps/cid/dev/ceremony-sign.ts` follow too).
 */
export function selectWrappedKey(wrapped: readonly WrappedOrgKey[], keyId: string): WrappedOrgKey | null {
    let best: WrappedOrgKey | null = null;
    for (const w of wrapped) {
        if (w.keyId === keyId && (best === null || w.keyIndex < best.keyIndex)) best = w;
    }
    return best;
}

/**
 * The 64-byte hybrid seed of `keyId` for the resolved session: from the
 * session's own key bag, or an owner delegate's wrapped copy (lowest
 * keyIndex) unwrapped with the delegate's `attr_encrypt` key. Callers zero
 * the seed once the signature is made.
 */
export async function loadOrgSigningSeed(
    keySource: KeySource, keyId: string,
): Promise<{ ok: true; secretSeed: Uint8Array } | { ok: false; message: string }> {
    if (keySource === 'keybag') {
        const seed = keys.getPrivateKey(keyId);
        if (!seed) {
            return { ok: false, message: `The ${keyId} is not in this session's key bag. Unlock the account and try again.` };
        }
        return { ok: true, secretSeed: aes.fromBase64(seed) };
    }

    const unwrapKey = keys.getPrivateKey(DELEGATE_UNWRAP_KEY);
    if (!unwrapKey) {
        return { ok: false, message: 'Your own keys are not loaded. Unlock this device and try again.' };
    }

    let wrapped: WrappedOrgKey[];
    try {
        wrapped = await delegateApi.getWrappedOrgKeys();
    } catch {
        return { ok: false, message: 'Could not fetch the organization keys from the server.' };
    }

    const entry = selectWrappedKey(wrapped, keyId);
    if (!entry) {
        return {
            ok: false,
            message: `The server did not release the organization's ${keyId} to this delegate. Only owner delegates receive it.`,
        };
    }

    try {
        const seedB64 = await unwrapPrivateKey(entry.wrappedKey, aes.fromBase64(unwrapKey));
        return { ok: true, secretSeed: aes.fromBase64(seedB64) };
    } catch {
        return { ok: false, message: `The organization's ${keyId} could not be unwrapped with your keys.` };
    }
}

// ── the files ───────────────────────────────────────────────────────────

/** The combined `ed25519_sig(64) || mldsa65_sig(3309)` signature federation
 *  files carry — never CID's split-halves wrapper. */
export function signFederationBytes(message: Uint8Array, secretSeed: Uint8Array): Uint8Array {
    return wrapperToCombinedBytes(signHybrid(message, secretSeed));
}

/** The request file with the organization's countersignature added:
 *  `CertificateRequests.ToFile` bytes. */
export function countersignedRequestFile(
    request: SignedCertificateRequest, orgCid: string, signature: Uint8Array,
): Uint8Array {
    return certificateRequestFile({ ...request, organization: { orgCid, signature } });
}

/** The consent file of a consent document and its signature. */
export function consentFile(doc: CeremonyDocument, signature: Uint8Array): Uint8Array {
    switch (doc.kind) {
        case 'binding_consent': return bindingConsentFile(doc.consent, signature);
        case 'account_migration_consent': return accountMigrationConsentFile(doc.consent, signature);
        case 'bulk_migration_consent': return bulkMigrationConsentFile(doc.consent, signature);
        case 'certificate_countersignature': throw new Error('a certificate request is countersigned, not a consent');
    }
}

/** The name the downloaded file is offered under. */
export function ceremonyFileName(doc: CeremonyDocument): string {
    switch (doc.kind) {
        case 'certificate_countersignature': {
            const r = doc.request.request;
            return `${r.kind}-certificate-request-${r.operatorId}-countersigned.json`;
        }
        case 'binding_consent':
            return `binding-consent-${doc.consent.operatorId}-${doc.consent.orgCid}-seq${doc.consent.sequence}.json`;
        case 'account_migration_consent': {
            const c = doc.consent;
            return `migration-consent-${c.accountCid}-${c.fromOperatorId}-to-${c.toOperatorId}-seq${c.sequence}.json`;
        }
        case 'bulk_migration_consent': {
            const c = doc.consent;
            return `bulk-migration-consent-${c.fromOperatorId}-to-${c.toOperatorId}-seq${c.sequence}.json`;
        }
    }
}

export type CeremonySigningErrorCode = 'signer-mismatch' | 'key-mismatch' | 'self-check-failed';

export class CeremonySigningError extends Error {
    readonly code: CeremonySigningErrorCode;

    constructor(code: CeremonySigningErrorCode, message: string) {
        super(message);
        this.name = 'CeremonySigningError';
        this.code = code;
    }
}

export interface CeremonySigner {
    /** The organization or account whose key signs (the resolved signer). */
    signerCid: string;
    /** Its `main_signing_key` seed. The caller zeroes it afterwards. */
    secretSeed: Uint8Array;
    /** Its current `main_signing_key` as the node's directory publishes it;
     *  when given, the seed must be that key. */
    currentKey: Uint8Array | null;
}

export interface SignedCeremonyFile {
    bytes: Uint8Array;
    filename: string;
}

/**
 * Signs `doc` and writes the file `cl-root` reads. Before signing: the signer
 * is the one the document names, and the seed is the key the document embeds
 * (consents) and the organization's current key. After signing, the file is
 * read back as `cl-root` reads it and checked against the organization (or
 * account) key — a countersigned request with `checkCertificateRequest`
 * against that key, a consent by its own reader, which verifies the
 * signature against the embedded key. Nothing leaves when a check fails.
 */
export async function signCeremonyDocument(
    doc: CeremonyDocument, signer: CeremonySigner, signatures: SignatureVerifier,
): Promise<SignedCeremonyFile> {
    const need = signerNeedOf(doc);
    const named = need.kind === 'organization' ? need.orgCid : need.accountCid;
    if (named !== null && named !== signer.signerCid) {
        throw new CeremonySigningError('signer-mismatch',
            `This document is signed by ${named}, not by ${signer.signerCid}. Nothing was signed.`);
    }

    const publicKey = signingPublicKeyFromSeed(signer.secretSeed);
    if (signer.currentKey !== null && !sameBytes(publicKey, signer.currentKey)) {
        throw new CeremonySigningError('key-mismatch',
            `The signing key this session holds is not ${signer.signerCid}'s current signing key as this node publishes it. `
            + 'Nothing was signed. Sign in again; if it persists, the organization\'s key copies are out of step.');
    }

    if (doc.kind === 'certificate_countersignature') {
        const request = doc.request.request;
        const signature = signFederationBytes(
            certificateCountersignatureBytes(request, signer.signerCid), signer.secretSeed);
        const bytes = countersignedRequestFile(doc.request, signer.signerCid, signature);
        const refusal = await checkCertificateRequest(readCertificateRequestFile(bytes),
            { orgCid: signer.signerCid, publicKey, signingProfile: HYBRID_PROFILE }, signatures);
        if (refusal !== null) {
            throw new CeremonySigningError('self-check-failed',
                `The countersigned request does not verify against the organization key (${refusal.code}: ${refusal.detail}). `
                + 'Nothing was downloaded.');
        }
        return { bytes, filename: ceremonyFileName(doc) };
    }

    const embedded = doc.kind === 'account_migration_consent' ? doc.consent.accountPublicKey : doc.consent.orgPublicKey;
    if (!sameBytes(publicKey, embedded)) {
        throw new CeremonySigningError('key-mismatch',
            'This consent names a different signing key than the one this session holds, so the Developer could not '
            + 'verify it. Nothing was signed. Prepare the consent again.');
    }

    let bytes: Uint8Array;
    try {
        switch (doc.kind) {
            case 'binding_consent':
                bytes = bindingConsentFile(doc.consent, signFederationBytes(bindingConsentSigningBytes(doc.consent), signer.secretSeed));
                await readBindingConsentFile(bytes, signatures);
                break;
            case 'account_migration_consent':
                bytes = accountMigrationConsentFile(doc.consent,
                    signFederationBytes(accountMigrationConsentSigningBytes(doc.consent), signer.secretSeed));
                await readAccountMigrationConsentFile(bytes, signatures);
                break;
            case 'bulk_migration_consent':
                bytes = bulkMigrationConsentFile(doc.consent,
                    signFederationBytes(bulkMigrationConsentSigningBytes(doc.consent), signer.secretSeed));
                await readBulkMigrationConsentFile(bytes, signatures);
                break;
        }
    } catch (e) {
        throw new CeremonySigningError('self-check-failed',
            `The signed consent does not read back as the Developer would read it (${(e as Error).message}). Nothing was downloaded.`);
    }
    return { bytes, filename: ceremonyFileName(doc) };
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
}
